import React, { useRef, useState } from "react";
import { GripVertical, ChevronUp, ChevronDown } from "lucide-react";

/**
 * Drag-and-drop ordering (native HTML5, no dependency) with up/down buttons as a keyboard/touch fallback.
 * renderItem(item, index, handle) -> node; `handle` is the grip + arrows control to place in the row.
 */
export default function SortableList({ items, onChange, keyOf = (i) => i.id, renderItem, className = "space-y-2", testId }) {
  const [over, setOver] = useState(null);
  const dragFrom = useRef(null);

  const move = (from, to) => {
    if (to < 0 || to >= items.length || from === to) return;
    const next = [...items];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    onChange(next);
  };

  return (
    <div className={className} data-testid={testId}>
      {items.map((item, i) => {
        const handle = (
          <div className="flex items-center shrink-0 text-slate-400">
            <span className="cursor-grab active:cursor-grabbing p-1 hover:text-slate-600" title="Drag to reorder" aria-hidden><GripVertical size={16} /></span>
            <div className="flex flex-col -ml-1">
              <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} aria-label="Move up" className="hover:text-blue-600 disabled:opacity-30"><ChevronUp size={13} /></button>
              <button type="button" onClick={() => move(i, i + 1)} disabled={i === items.length - 1} aria-label="Move down" className="hover:text-blue-600 disabled:opacity-30"><ChevronDown size={13} /></button>
            </div>
          </div>
        );
        return (
          <div key={keyOf(item)} draggable
            onDragStart={(e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(e) => { e.preventDefault(); setOver(i); }}
            onDragLeave={() => setOver((o) => (o === i ? null : o))}
            onDrop={(e) => { e.preventDefault(); const from = dragFrom.current; dragFrom.current = null; setOver(null); if (from !== null) move(from, i); }}
            onDragEnd={() => { dragFrom.current = null; setOver(null); }}
            className={`transition-shadow ${over === i && dragFrom.current !== null && dragFrom.current !== i ? "ring-2 ring-blue-400 rounded-lg" : ""}`}>
            {renderItem(item, i, handle)}
          </div>
        );
      })}
    </div>
  );
}
